import { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  setPracticeData,
  setSummaryData,
} from "../../../../store/features/CreatePractice";
import { Container } from "../style";
import {
  Button,
  Form,
  Input,
  Progress,
  message,
  Alert,
  Select,
} from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
import ScheduleMenu from "../../../../components/ScheduleMenu/index";
import MockPhone from "../../../../components/MockPhone/index";
import iconCongrats from "../../../../assets/icon_congrats.png";
import { gql, useMutation, useLazyQuery } from "@apollo/client";
import useSubscriptionCheck from "../../../../hooks/useSubscriptionCheck";
import SubscriptionModal from "../../../../components/SubscriptionModal";

const { TextArea } = Input;

const CREATE_PRACTICE = gql`
  mutation createPractice($createPracticeInput: CreatePracticeInput!) {
    createPractice(payload: $createPracticeInput) {
      _id
    }
  }
`;
const UPDATE_PRACTICE = gql`
  mutation UpdatePractice($payload: UpdatePracticeInput!) {
    updatePractice(payload: $payload) {
      _id
      about
      description
      hint
      imageUrl
      link
      name
      rate
      publishedAt
      visibility
      wrap_up
    }
  }
`;
const CREATE_MULTIPLE_QUESTIONS = gql`
  mutation createMultipleQuestions($payload: CreateMultipleQuestionsInput!) {
    createMultipleQuestions(payload: $payload) {
      _id
      title
      placeholder
      description
      order
      extraData
      practiceId
    }
  }
`;
const UPDATE_MULTIPLE_QUESTIONS = gql`
  mutation updateMultipleQuestions($payload: UpdateMultipleQuestionsInput!) {
    updateMultipleQuestions(payload: $payload) {
      _id
      title
      placeholder
      description
      order
      extraData
    }
  }
`;

const GROUPS_BY_COACH = gql`
  query GroupsByCoach($coachId: String!, $isArchived: Boolean) {
    groupsByCoach(coachId: $coachId, isArchived: $isArchived) {
      _id
      name
    }
  }
`;

const typeList = {
  desc: "TEXT_ONLY",
  short: "ONE_QUESTION_ONE_INPUT_TO_ANSWER",
  multi: "ONE_QUESTION_MANY_OPTION_TO_SELECT",
};

export default function WrapForm({ onBack, onComplete }) {
  const userInfo = useSelector((state) => state.userInfo.userInfo);
  const summaryData = useSelector((state) => state.practice.summaryData);
  const editData = useSelector((state) => state.practice.editData);
  const editAddData = useSelector((state) => state.practice.editAddData);
  // eslint-disable-next-line no-unused-vars
  const pages = useSelector((state) => state.practice.pages);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [createPractice] = useMutation(CREATE_PRACTICE);
  const [updatePractice] = useMutation(UPDATE_PRACTICE);
  const [createMultipleQuestions] = useMutation(CREATE_MULTIPLE_QUESTIONS);
  const [updateMultipleQuestions] = useMutation(UPDATE_MULTIPLE_QUESTIONS);
  const rowData = useSelector((state) => state.practice.practiceData);
  const paramId = rowData?._id;
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);

  const [fetchGroups] = useLazyQuery(GROUPS_BY_COACH, {
    variables: { coachId: userInfo.coach?._id, isArchived: false },
    onCompleted: (data) => {
      if (data?.groupsByCoach) {
        setGroups(data.groupsByCoach);
      }
    },
    fetchPolicy: "network-only",
  });

  // Fetch groups on mount
  useEffect(() => {
    if (userInfo.coach?._id) {
      fetchGroups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo.coach?._id]);

  // Subscription check
  const {
    showModal,
    checkAndExecute,
    handleSubscriptionSuccess,
    handleModalCancel,
    upgradePromptKind,
  } = useSubscriptionCheck();

  // Form - must be defined before useEffect that uses it
  const [form] = Form.useForm();

  useEffect(() => {
    if (!form) return;

    let visibilityValue = summaryData.visibility || "ALL_SUBSCRIBERS";
    if (
      !summaryData.visibility &&
      rowData.visibilityType === "GROUP" &&
      rowData.visibilityGroupId
    ) {
      visibilityValue = `GROUP_${rowData.visibilityGroupId}`;
    }

    form.setFieldsValue({
      wrap_up: summaryData.wrap_up,
      visibility: visibilityValue,
    });
    // 仅在进入本步骤时初始化，避免 summaryData 同步后覆盖用户选择
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);
  const wrap_up_value = Form.useWatch("wrap_up", form);
  const visibleValue = Form.useWatch("visibility", form);

  const updateSummaryData = useCallback(() => {
    if (!visibleValue) return;

    dispatch(
      setSummaryData({
        ...summaryData,
        wrap_up: wrap_up_value,
        visibility: visibleValue,
      })
    );
  }, [wrap_up_value, visibleValue, dispatch, summaryData]);

  useEffect(() => {
    updateSummaryData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrap_up_value, visibleValue]);

  console.log(summaryData, editData, editAddData);
  // param: values, status
  const onSubmit = async ({ values, status, scheduledAt }) => {
    setLoading(true);
    let practiceId = "";
    try {
      // Get practice data
      let param = {
        // active: true,
        createdBy: userInfo.coach?._id,
        ...summaryData,
        ...values,
        status,
        visibility: "PRIVATE",
        visibilityType: "ALL_SUBSCRIBERS",
      };

      // 根据选择设置 visibility 相关参数
      const visibilityValue = values.visibility;

      if (visibilityValue?.startsWith("GROUP_")) {
        param.visibilityType = "GROUP";
        param.visibilityGroupId = visibilityValue.replace("GROUP_", "");
      }

      if (scheduledAt) {
        param.scheduledAt = scheduledAt;
      }
      if (!paramId) {
        // Create practice
        const res = await createPractice({
          variables: {
            createPracticeInput: param,
          },
        });
        practiceId = res?.data?.createPractice?._id;
      } else {
        // Update practice
        delete param.active;
        delete param.createdBy;
        param._id = paramId;
        await updatePractice({
          variables: {
            payload: param,
          },
        });
        practiceId = paramId;
      }
      // Get questions data
      let questions = [];
      questions.push({
        order: 1,
        practiceId,
        extraData: `{"duration":"${editData.duration}"}`,
        title: editData.title,
        description: editData.description,
        type: "TEXT_ONLY",
      });
      // Update without practiceId
      if (paramId) {
        questions[0]._id = editData._id;
        delete questions[0]?.practiceId;
      }
      editAddData.length &&
        editAddData.forEach((v, i) => {
          let extraData = "{";
          // if (v.subtitle) {
          //   extraData += `"subtitle":"${v.subtitle}"`;
          // }
          if (v?.options?.length > 0 && v?.options[0]) {
            if (extraData.length > 2) extraData += ",";
            extraData += `"options":[`;
            v.options.forEach((option, optionIndex) => {
              extraData += `{"key":"${option}","value":"${option}"}`;
              if (optionIndex < v.options.length - 1) {
                extraData += ",";
              }
            });
            extraData += "]";
          }
          extraData += "}";
          let obj = {
            order: parseInt(i) + 2,
            practiceId,
            type: typeList[v.layout],
            title: v.title,
            description: v.description,
            extraData,
          };
          // Update without practiceId
          if (paramId) {
            obj._id = v._id;
            delete obj.practiceId;
          }
          questions.push(obj);
        });
      if (!paramId) {
        // Create multiple questions
        await createMultipleQuestions({
          variables: {
            payload: {
              questions,
            },
          },
        });
        message.success("Practice has been created!");
      } else {
        // Update multiple questions
        await updateMultipleQuestions({
          variables: {
            payload: {
              questions,
            },
          },
        });
        message.success("Practice has been updated!");
      }
      dispatch(setPracticeData({}));
      navigate("/contentManager?tabName=1");
    } finally {
      setLoading(false);
    }
    setSummaryData(
      Object.assign({}, summaryData, {
        wrap_up: wrap_up_value,
      })
    );
    onComplete();
  };

  const onFinish = async (values) => {
    console.log("Success:", values);
    // Check subscription before publishing
    checkAndExecute(() => {
      onSubmit({ values, status: "PUBLISHED" });
    });
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
    setLoading(false);
  };

  const onDraft = () => {
    const values = form.getFieldsValue();
    onSubmit({ values, status: "DRAFTED" });
  };

  return (
    <Container>
      <div className="page-form">
        <div className="left-side">
          <div className="page-title">Wrap Up Practice</div>
          {/* <div className="nav-line flex-around">
            <Button
              icon={<CaretLeftOutlined />}
              type="text"
              onClick={onClickPrev}
            ></Button>
            <div className="text-center">{`Page${pages + 1}`}</div>
            <Button icon={<CaretRightOutlined />} type="text" disabled></Button>
          </div> */}
          <Form
            form={form}
            name="wrapForm"
            layout="vertical"
            size="large"
            className="full-width"
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
          >
            <Form.Item label="Body content (Optional)" name="wrap_up">
              <TextArea
                showCount
                maxLength={200}
                placeholder="Input wrap up body content"
                style={{ resize: "none" }}
              />
            </Form.Item>
            <Form.Item
              label="Visible to"
              name="visibility"
              rules={[{ required: true, message: "Please select visibility" }]}
            >
              <Select placeholder="Select visibility" size="large">
                <Select.Option value="ALL_SUBSCRIBERS">
                  All Subscribers
                </Select.Option>
                {groups.map((group) => (
                  <Select.Option key={group._id} value={`GROUP_${group._id}`}>
                    {group.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <br />
            {summaryData.status === "SCHEDULED" && summaryData.scheduledAt && (
              <Alert
                className="full-width clock-tag"
                message={`This practice is scheduled to be published at ${new Date(
                  summaryData.scheduledAt
                ).toLocaleString()}.`}
                icon={<ClockCircleOutlined />}
                type="success"
                showIcon
              />
            )}
            <Form.Item>
              <ScheduleMenu
                id={paramId}
                showSchedule={!paramId || summaryData.status !== "PUBLISHED"}
                form={form}
                loading={loading}
                onSubmit={onSubmit}
              ></ScheduleMenu>
              <Button
                block
                className="mt-16"
                onClick={onDraft}
                loading={loading}
              >
                Save as draft
              </Button>
            </Form.Item>
          </Form>
        </div>
        <div className="right-side">
          <MockPhone
            title={(summaryData && summaryData.name) || "Practice Name"}
          >
            <Progress
              className="mock-progress"
              percent={100}
              showInfo={false}
              strokeLinecap="square"
              strokeColor={"#547756"}
            />
            <img src={iconCongrats} alt="Congrats" className="mock-congrats" />
            <div className="complete-hint">
              <p>Congrats!</p>
              <p>You just completed the practice.</p>
            </div>

            {wrap_up_value && (
              <pre className="wrap-body">{wrap_up_value || "Body content"}</pre>
            )}
            <Button block type="primary" size="large">
              Complete
            </Button>
          </MockPhone>
        </div>
      </div>
      <SubscriptionModal
        open={showModal}
        onSuccess={handleSubscriptionSuccess}
        onCancel={handleModalCancel}
        checkoutPromptKind={upgradePromptKind ?? "trial"}
      />
    </Container>
  );
}
